import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  fetchSessionConfig,
  forceClientLogout,
  getSessionIdFromToken,
  getStoredSessionHeartbeatIntervalMs,
  getStoredSessionTimeoutMs,
  sendSessionHeartbeat,
  sendSessionLogoutBeacon,
} from "../lib/sessionTracking";
import { getJWTTokenFromCookie } from "../utils";

const activityEvents = [
  // "mousemove",
  // "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

function SessionTracker() {
  const authToken = getJWTTokenFromCookie();
  const user = useSelector((state) => state.user);
  const lastActivityRef = useRef(Date.now());
  const logoutInProgressRef = useRef(false);
  const heartbeatIntervalMsRef = useRef(getStoredSessionHeartbeatIntervalMs());
  const sessionTimeoutMsRef = useRef(getStoredSessionTimeoutMs());

  useEffect(() => {
    if (!authToken || !getSessionIdFromToken() || (user.id && user.isDefault)) {
      return undefined;
    }

    lastActivityRef.current = Date.now();
    logoutInProgressRef.current = false;
    heartbeatIntervalMsRef.current = getStoredSessionHeartbeatIntervalMs();
    sessionTimeoutMsRef.current = getStoredSessionTimeoutMs();
    let heartbeatTimer = null;

    const markActive = () => {
      lastActivityRef.current = Date.now();
    };

    const startHeartbeatTimer = (intervalMs) => {
      heartbeatIntervalMsRef.current = intervalMs;

      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
      }

      heartbeatTimer = window.setInterval(() => {
        if (
          logoutInProgressRef.current ||
          !getJWTTokenFromCookie() ||
          !getSessionIdFromToken()
        ) {
          return;
        }

        const inactiveForMs = Date.now() - lastActivityRef.current;
        if (inactiveForMs >= sessionTimeoutMsRef.current) {
          logoutInProgressRef.current = true;
          forceClientLogout("inactive_timeout");
          return;
        }

        sendSessionHeartbeat().catch(() => {});
      }, intervalMs);
    };

    startHeartbeatTimer(heartbeatIntervalMsRef.current);

    fetchSessionConfig()
      .then(({ sessionHeartbeatIntervalMs, sessionTimeoutMs }) => {
        startHeartbeatTimer(sessionHeartbeatIntervalMs);
        sessionTimeoutMsRef.current = sessionTimeoutMs;
      })
      .catch(() => {});

    const handlePageHide = () => {
      if (
        logoutInProgressRef.current ||
        !getJWTTokenFromCookie() ||
        !getSessionIdFromToken()
      ) {
        return;
      }

      logoutInProgressRef.current = true;
      sendSessionLogoutBeacon("browser_closed");
    };

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markActive);
    });
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      if (heartbeatTimer) {
        window.clearInterval(heartbeatTimer);
      }
      window.removeEventListener("pagehide", handlePageHide);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markActive);
      });
    };
  }, [authToken, user.id, user.isDefault]);

  return null;
}

export default SessionTracker;
